import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Mail, Phone, MessageSquare, CheckCircle, MapPin, Building2, ExternalLink, AlertCircle, PlayCircle, Loader2, FileText } from 'lucide-react'
import { WaveIcon } from '@/components/common/WaveIcon'
import { engineApiClient, apiRequest } from '@/lib/apiClient'
import type { CandidateScreeningResult, CandidateScoreBand, CandidateEvent } from '@/lib/contracts'
import { engineEndpoints, SCORE_BAND_CONFIG, SOURCE_CONFIG, scoreToBand } from '@/lib/contracts'

const FALLBACK_PROJECT_ID = 'alpha-grid'
const FALLBACK_CANDIDATE_ID = 'layla-haddad'

// ─── Pipeline stage badge ─────────────────────────────────────────────────────

function getPipelineStageBadgeClass(stage: string): string {
  switch (stage.toLowerCase()) {
    case 'identified':
    case 'applied':
      return 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
    case 'prepared':
    case 'outreach':
      return 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
    case 'shortlisted':
      return 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
    case 'interview':
      return 'bg-green-500/10 text-green-400 border border-green-500/20'
    default:
      return 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
  }
}

function capitalise(s: string): string {
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** Ensure LinkedIn (and any) URLs have a protocol prefix */
function ensureHttps(url?: string): string | undefined {
  if (!url) return undefined
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  return `https://${url}`
}

// ─── Data provenance banner for sourced candidates ───────────────────────────

function ApolloProvenanceBanner({ linkedinUrl, locationText }: { linkedinUrl?: string; locationText?: string }) {
  return (
    <div className="flex items-start gap-2.5 bg-blue-500/5 border border-blue-500/20 rounded-lg px-4 py-3 text-xs text-blue-300 mb-6">
      <WaveIcon size="sm" className="shrink-0 mt-0.5" />
      <div className="space-y-1">
        <p className="font-medium text-blue-300">Assessed from AI enriched data</p>
        <p className="text-blue-400/80 leading-relaxed">
          This score is based on structured profile data — job title, career history, company, location, and seniority. No CV or written application has been reviewed. The score reflects profile-level fit only.
        </p>
        {linkedinUrl && (
          <a
            href={ensureHttps(linkedinUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline transition-colors mt-1"
          >
            View LinkedIn profile <ExternalLink size={11} />
          </a>
        )}
        {locationText && (
          <p className="text-blue-400/60 flex items-center gap-1 mt-0.5">
            <MapPin size={10} /> {locationText}
          </p>
        )}
      </div>
    </div>
  )
}

// ─── Career summary card ──────────────────────────────────────────────────────

function parseCareerBullets(summary: string): string[] {
  const raw = summary
    .replace(/\|/g, '\n')
    .replace(/\s*@\s*/g, ' ')
    .replace(/\.\s+(?=[A-Z])/g, '.\n')
    .split(/\n+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 10)
    .filter((s) => !s.startsWith('Function:') && !s.includes('master_'))

  const seen = new Set<string>()
  return raw.filter((s) => {
    const key = s.toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  }).slice(0, 8)
}

function CareerSummaryCard({ summary }: { summary?: string }) {
  if (!summary) return null
  const bullets = parseCareerBullets(summary)
  if (bullets.length === 0) return null

  return (
    <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
      <p className="text-xs uppercase text-gray-400 font-medium mb-3">Career Summary</p>
      {bullets.length === 1 ? (
        <p className="text-gray-300 text-sm leading-relaxed">{bullets[0]}</p>
      ) : (
        <ul className="space-y-1.5">
          {bullets.map((bullet, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FD802E]/60 shrink-0" />
              <span className="leading-relaxed">{bullet}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Activity Timeline ────────────────────────────────────────────────────────

const EVENT_LABELS: Record<string, string> = {
  email_sent:            '📧 Outreach email sent',
  email_opened:          '👀 Email opened',
  link_clicked:          '🔗 Magic link clicked',
  pre_screen_started:    '▶️ Pre-screen started',
  pre_screen_completed:  '✅ Pre-screen completed',
  follow_up_sent:        '📧 Follow-up sent',
  declined_interest:     '❌ Declined interest',
  sourced:               '⚡ Added to pipeline',
}

function getEventLabel(eventType: string): string {
  return EVENT_LABELS[eventType] ?? '⚡ Added to pipeline'
}

function formatEventDate(isoString: string): string {
  const d = new Date(isoString)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function ActivityTimeline({
  events,
  candidateCreatedAt,
}: {
  events: CandidateEvent[]
  candidateCreatedAt?: string
}) {
  // If no events from the events table, synthesise a "Added to pipeline" from created_at
  const displayEvents: Array<{ event_type: string; created_at: string }> =
    events.length > 0
      ? events
      : candidateCreatedAt
        ? [{ event_type: 'sourced', created_at: candidateCreatedAt }]
        : []

  if (displayEvents.length === 0) return null

  return (
    <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
      <p className="text-xs uppercase text-gray-400 font-medium mb-4">Activity Timeline</p>
      <div className="relative">
        {/* Vertical connecting line */}
        {displayEvents.length > 1 && (
          <div className="absolute left-[5px] top-3 bottom-3 w-px bg-[#2a4a5c]" />
        )}
        <ul className="space-y-3.5">
          {displayEvents.map((event, i) => (
            <li key={i} className="flex items-center gap-3 relative">
              {/* Dot */}
              <span className="w-3 h-3 rounded-full bg-[#FD802E]/70 border border-[#FD802E]/50 shrink-0 z-10" />
              <span className="flex-1 text-sm text-gray-300 leading-snug">
                {getEventLabel(event.event_type)}
              </span>
              <span className="text-xs text-gray-500 shrink-0 tabular-nums">
                {formatEventDate(event.created_at)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// ─── Pre-Screen Feedback section ──────────────────────────────────────────────

function PreScreenFeedback({
  matchedHighlights,
  gaps,
  isSourced,
}: {
  matchedHighlights: string[]
  gaps: string[]
  isSourced: boolean
}) {
  const hasData = matchedHighlights.length > 0 || gaps.length > 0

  if (!hasData) {
    return (
      <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
        <p className="text-xs uppercase text-gray-400 font-medium mb-4">Pre-Screen Feedback</p>
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <PlayCircle size={28} className="text-gray-600" />
          <p className="text-gray-500 text-sm font-medium">Pre-screen interview not yet completed</p>
          <p className="text-gray-600 text-xs italic leading-relaxed max-w-xs">
            This section will populate with Erica's findings once the candidate completes their pre-screen conversation.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-xs uppercase text-gray-400 font-medium px-1">Pre-Screen Feedback</p>

      {matchedHighlights.length > 0 && (
        <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase text-gray-400 font-medium">
              ✅ Erica Pre-Screen: Strengths ({matchedHighlights.length})
            </p>
          </div>
          <ul className="space-y-2">
            {matchedHighlights.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle size={14} className="text-green-400 mt-0.5 shrink-0" />
                <span className="text-gray-300">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {gaps.length > 0 && (
        <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
          <p className="text-xs uppercase text-gray-400 font-medium mb-3">
            ⚠️ Erica Pre-Screen: Areas to Probe ({gaps.length})
          </p>
          <ul className="space-y-2">
            {gaps.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <span className="text-[#FD802E] mt-0.5 shrink-0">△</span>
                <span className="text-gray-300">{item}</span>
              </li>
            ))}
          </ul>
          {isSourced && (
            <p className="text-gray-600 text-xs mt-3 border-t border-[#2a4a5c] pt-3">
              Gaps reflect what's absent from the profile data — not necessarily what the candidate lacks. Use pre-screen to validate.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const CandidateReview = () => {
  const { id, candidateId } = useParams<{ id: string; candidateId: string }>()
  const projectId = id ?? FALLBACK_PROJECT_ID
  const candId = candidateId ?? FALLBACK_CANDIDATE_ID
  const navigate = useNavigate()

  const [screening,    setScreening]    = useState<CandidateScreeningResult | null>(null)
  const [isLoading,    setIsLoading]    = useState(true)
  const [error,        setError]        = useState<string | null>(null)
  const [retryCount,   setRetryCount]   = useState(0)
  const [notes,        setNotes]        = useState('')
  const [bandSaving,   setBandSaving]   = useState(false)
  const [bandOverride, setBandOverride] = useState<CandidateScoreBand | null>(null)
  const [events,       setEvents]       = useState<CandidateEvent[]>([])

  useEffect(() => {
    setIsLoading(true)
    engineApiClient
      .getCandidateScreening(projectId, candId)
      .then(setScreening)
      .catch(() => setError('Unable to load candidate screening. Please try again.'))
      .finally(() => setIsLoading(false))
  }, [projectId, candId, retryCount])

  // Fetch activity events (non-blocking — don't show error if this fails)
  useEffect(() => {
    engineApiClient
      .getCandidateEvents(projectId, candId)
      .then((res) => setEvents(res.events ?? []))
      .catch(() => setEvents([]))
  }, [projectId, candId])

  const initials = screening?.candidateName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? '??'

  const isSourced         = screening?.source === 'sourced'
  const isKeywordFallback = screening?.rationale?.includes('keyword matching')

  // Parse title/company from "Title, Company" format
  const rawTitle  = screening?.candidateTitle ?? ''
  const commaIdx  = rawTitle.indexOf(', ')
  const roleTitle = commaIdx > -1 ? rawTitle.slice(0, commaIdx) : rawTitle
  const company   = commaIdx > -1 ? rawTitle.slice(commaIdx + 2) : ''

  // Score band (respects recruiter override)
  const activeBand: CandidateScoreBand =
    bandOverride ?? (screening?.scoreBand as CandidateScoreBand) ?? scoreToBand(screening?.fitScore ?? 0)
  const bandCfg = SCORE_BAND_CONFIG[activeBand]

  return (
    <div className="bg-[#02182B] min-h-screen">
      {/* Page header */}
      <div className="border-b border-[#233D4C]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">Candidate Profile</h1>
            <p className="text-gray-400 text-sm mt-0.5">AI-enriched profile · fit assessment · pre-screen readiness</p>
          </div>
          <button
            onClick={() => navigate(`/project/${projectId}/dashboard`)}
            className="text-gray-400 hover:text-[#FD802E] text-sm transition-colors"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {isLoading ? (
          <div className="py-16 flex flex-col items-center justify-center gap-3 text-gray-400">
            <Loader2 size={24} className="animate-spin text-[#FD802E]" />
            <span className="text-sm">Loading candidate profile…</span>
          </div>
        ) : error ? (
          <div className="max-w-md mx-auto mt-16 text-center space-y-4">
            <div className="w-12 h-12 bg-red-500/10 rounded-full flex items-center justify-center mx-auto">
              <AlertCircle size={24} className="text-red-400" />
            </div>
            <p className="text-red-400 text-sm font-medium">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => navigate(`/project/${projectId}/dashboard`)}
                className="bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                ← Go back
              </button>
              <button
                onClick={() => { setError(null); setRetryCount(c => c + 1) }}
                className="bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors"
              >
                Try again
              </button>
            </div>
          </div>
        ) : screening ? (
          <>
            {/* Apollo provenance banner — only for sourced candidates */}
            {isSourced && (
              <ApolloProvenanceBanner
                linkedinUrl={screening.linkedinUrl}
                locationText={screening.locationText}
              />
            )}

            {/* Keyword fallback warning */}
            {isKeywordFallback && (
              <div className="flex items-center gap-2 bg-yellow-500/5 border border-yellow-500/20 rounded-lg px-4 py-3 text-xs text-yellow-300 mb-6">
                <AlertCircle size={13} className="shrink-0 text-yellow-400" />
                <p>This score was generated by keyword matching — LLM ranking was unavailable when this candidate was processed. Run a fresh Headhunt sprint to get a full AI assessment.</p>
              </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6">

              {/* ── Left — Identity ───────────────────────────────────────── */}
              <div className="space-y-4">

                {/* Profile card (header) */}
                <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">

                  {/* Top row: avatar + name | score */}
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-12 h-12 bg-[#FD802E] rounded-full flex items-center justify-center text-[#233D4C] font-bold text-base shrink-0">
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-white font-bold text-base leading-tight">{screening.candidateName}</h2>
                      {roleTitle && <p className="text-gray-400 text-sm mt-0.5 truncate">{roleTitle}</p>}
                    </div>
                    {/* Score — large number with band colour */}
                    <div className="shrink-0 text-right">
                      <p className={`text-2xl font-bold leading-none ${bandCfg.color}`}>
                        {screening.fitScore}
                      </p>
                      <p className={`text-xs font-medium mt-0.5 ${bandCfg.color}`}>
                        {bandCfg.label}
                      </p>
                    </div>
                  </div>

                  {/* Company */}
                  {company && (
                    <div className="flex items-center gap-2 text-sm text-gray-300 mb-2">
                      <Building2 size={13} className="text-gray-500 shrink-0" />
                      <span>{company}</span>
                    </div>
                  )}

                  {/* Location */}
                  {screening.locationText && (
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                      <MapPin size={12} className="text-[#FD802E]/60 shrink-0" />
                      <span>{screening.locationText}</span>
                    </div>
                  )}

                  {/* Contact details — email + phone */}
                  {(screening.email || screening.phone) && (
                    <div className="bg-[#02182B]/60 border border-[#2a4a5c] rounded-lg px-3 py-2.5 mb-3 space-y-1.5">
                      <p className="text-[10px] uppercase text-gray-500 font-medium mb-1">Contact</p>
                      {screening.email && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Mail size={12} className="text-gray-500 shrink-0" />
                          <a
                            href={`mailto:${screening.email}`}
                            className="hover:text-white transition-colors truncate text-xs"
                          >
                            {screening.email}
                          </a>
                        </div>
                      )}
                      {screening.phone && (
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Phone size={12} className="text-gray-500 shrink-0" />
                          <a
                            href={`tel:${screening.phone}`}
                            className="hover:text-white transition-colors text-xs"
                          >
                            {screening.phone}
                          </a>
                        </div>
                      )}
                      {!screening.email && !screening.phone && (
                        <p className="text-xs text-gray-600 italic">Contact details will appear here once the CV is uploaded or Database data is available.</p>
                      )}
                    </div>
                  )}
                  {!screening.email && !screening.phone && (
                    <div className="bg-[#02182B]/60 border border-[#2a4a5c]/50 rounded-lg px-3 py-2 mb-3">
                      <p className="text-[10px] uppercase text-gray-500 font-medium mb-1">Contact</p>
                      <p className="text-xs text-gray-600 italic">No contact details yet — will populate from CV or Database at outreach time.</p>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    {screening.linkedinUrl && (
                      <a
                        href={ensureHttps(screening.linkedinUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-xs px-3 py-1.5 rounded-lg hover:border-[#FD802E]/40 transition-colors"
                      >
                        LinkedIn <ExternalLink size={11} />
                      </a>
                    )}
                    {screening.cvUrl && (
                      <a
                        href={screening.cvUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-xs px-3 py-1.5 rounded-lg hover:border-[#FD802E]/40 transition-colors"
                      >
                        <FileText size={12} className="text-[#FD802E]" />
                        View CV
                      </a>
                    )}
                  </div>

                  {/* Pills row: Source + Pipeline stage + Score band */}
                  <div className="flex flex-wrap gap-1.5">
                    {/* Source pill */}
                    {screening.source && SOURCE_CONFIG[screening.source] && (
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border border-transparent ${SOURCE_CONFIG[screening.source].bg} ${SOURCE_CONFIG[screening.source].color}`}>
                        {SOURCE_CONFIG[screening.source].label}
                      </span>
                    )}
                    {/* Pipeline stage pill */}
                    {screening.pipelineStage && (
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getPipelineStageBadgeClass(screening.pipelineStage)}`}>
                        {capitalise(screening.pipelineStage)}
                      </span>
                    )}
                    {/* Score band pill */}
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${bandCfg.bg} ${bandCfg.color} ${bandCfg.border}`}>
                      {bandCfg.label}
                    </span>
                  </div>
                </div>

                {/* Score band override */}
                {screening.scoreBand && (() => {
                  const bands: CandidateScoreBand[] = ['low_fit', 'review', 'qualify', 'strong_fit']
                  return (
                    <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-2">Override band</p>
                      <div className="flex gap-1.5">
                        {bands.map((b) => {
                          const cfg = SCORE_BAND_CONFIG[b]
                          const isActive = activeBand === b
                          return (
                            <button
                              key={b}
                              disabled={bandSaving}
                              onClick={async () => {
                                if (isActive) return
                                setBandSaving(true)
                                await apiRequest({ path: engineEndpoints.candidateScoreBand(projectId, candId), method: 'PATCH', body: { scoreBand: b } }).catch(() => null)
                                setBandOverride(b)
                                setBandSaving(false)
                              }}
                              className={`px-2.5 py-1 rounded text-xs font-medium border transition-all ${isActive ? `${cfg.bg} ${cfg.color} border-current opacity-100` : 'bg-transparent border-[#2a4a5c] text-gray-500 hover:text-gray-300'}`}
                            >
                              {bandSaving && isActive ? '…' : cfg.label}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })()}

              </div>

              {/* ── Centre + Right — Assessment, Career, Timeline, Pre-Screen, Notes ── */}
              <div className="lg:col-span-2 space-y-4">

                {/* Erica Profile Assessment */}
                <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-5">
                  <p className="text-xs uppercase text-gray-400 font-medium mb-2">Erica Profile Assessment</p>
                  <p className="text-gray-300 text-sm leading-relaxed">{screening.rationale}</p>
                </div>

                {/* Career Summary */}
                <CareerSummaryCard summary={screening.summary} />

                {/* Activity Timeline */}
                <ActivityTimeline
                  events={events}
                  candidateCreatedAt={screening.created_at}
                />

                {/* Pre-Screen Feedback */}
                <PreScreenFeedback
                  matchedHighlights={screening.matchedHighlights}
                  gaps={screening.gaps}
                  isSourced={isSourced}
                />

                {/* Recruiter Notes */}
                <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <MessageSquare size={13} className="text-[#FD802E]" />
                    <p className="text-xs uppercase text-gray-400 font-medium">Recruiter Notes</p>
                  </div>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Capture interview notes, insights, and follow-ups…"
                    className="w-full min-h-[100px] bg-[#02182B] border border-[#2a4a5c] rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#FD802E] transition-colors resize-none"
                  />
                  <button className="w-full bg-[#02182B] border border-[#2a4a5c] text-white text-sm py-2 rounded-lg hover:border-[#FD802E]/40 transition-colors">
                    Save Notes
                  </button>
                </div>
              </div>

            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}
